'use client';

import SectionHeader from '@/app/(website)/components/SectionHeader';
import {Body} from '@/components/body';
import {Heading} from '@/components/heading';
import {useEffect, useRef, useState} from 'react';
import {cn} from '@/lib/utils';

const ROLES = [
  'RUP',
  'Uffici Gare e Contratti',
  'Dirigenti Amministrativi',
  'Legali Interni PA',
  'Responsabili Provveditorato',
  'Stazioni Appaltanti Comunali',
  'Enti Sanitari (ASL / AO)',
  'Centrali di Committenza',
  'Università ed Enti di Ricerca',
  'Autorità di Sistema Portuale',
];

const PROCEDURES = [
  'Affidamenti Diretti',
  'Procedure Negoziate',
  'Procedure Aperte',
  'Concessioni',
  'Accordi Quadro',
  'Appalti di Lavori',
  'Appalti di Servizi',
  'Appalti di Forniture',
  'OEPV e Prezzo più Basso',
  'Contenzioso Amm.vo',
];

function TagBadge({
  label,
  className,
  style,
}: {
  label: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={cn(
        'bg-background-4 rounded-2xl px-6 py-4 flex items-center justify-center',
        className,
      )}
      style={style}
    >
      <Body variant="bold" className="text-center">
        {label}
      </Body>
    </div>
  );
}

export default function PotentialMarketAppalti() {
  const [isVisible, setIsVisible] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {threshold: 0.1},
    );

    if (contentRef.current) {
      observer.observe(contentRef.current);
    }

    return () => {
      if (contentRef.current) {
        observer.unobserve(contentRef.current);
      }
    };
  }, []);

  return (
    <div className="flex flex-col w-full items-center gap-10 py-16 px-8">
      <SectionHeader
        badgeText="CHI USA NORMO.AI"
        title={
          <>
            Pensato per chi gestisce{' '}
            <span className="rounded-[2rem] rounded-br-[8px] py-0 px-2 md:px-4 bg-secondary text-primary">
              appalti pubblici
            </span>{' '}
            ogni giorno
          </>
        }
        subtitle="Normo.ai è particolarmente utile dove il lavoro richiede ricerca, analisi documentale, redazione e revisione continua."
        titleAnimationStart="bottom"
        className="px-8"
      />
      <div
        ref={contentRef}
        className={`grid grid-cols-1 md:grid-cols-2 gap-12 max-w-[56.625rem] w-full transition-all duration-1000 ease-in-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="flex flex-col gap-4">
          <Heading variant="h4" className="text-center md:text-left">
            Figure professionali
          </Heading>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ROLES.map((role, index) => (
              <TagBadge
                key={index}
                label={role}
                className={`transition-all duration-700 ease-in-out ${
                  isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
                }`}
                style={{transitionDelay: `${index * 60}ms`}}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <Heading variant="h4" className="text-center md:text-left">
            Tipologie di procedimento
          </Heading>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PROCEDURES.map((proc, index) => (
              <TagBadge
                key={index}
                label={proc}
                className={`transition-all duration-700 ease-in-out ${
                  isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
                }`}
                style={{transitionDelay: `${index * 60 + 300}ms`}}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
